import { useEffect, useState } from 'react';
import { useGlobal } from 'reactn';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import classNames from 'classnames';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import ReactResizeDetector from 'react-resize-detector';
import { get } from 'lodash';
import gql from 'graphql-tag';
import { Helmet } from 'react-helmet';
import { datadogRum } from '@datadog/browser-rum';
import TopBar from '../modules/topbar/components/TopBar';
import SideBar from '../modules/sidebar/components/SideBar';
import TimesheetsPage from '../modules/timesheets/components/TimesheetsPage';
import ProjectManagerPage from '../modules/project-manager/components/ProjectManagerPage';
import FinancePage from '../modules/finance/components/FinancePage';
import AdminPage from '../modules/admin/components/AdminPage';
import SuperAdminPage from '../modules/super-admin/components/Page';
import ProfilePage from '../modules/profile/components/ProfilePage';
import PerformancePage from '../modules/performance/components/PerformancePage';
import HumanResourcesPage from '../modules/human-resources/components/HumanResourcesPage';
import TextLoader from '../modules/common/components/TextLoader';
// import FeedbackButton from '../modules/feedback/components/FeedbackButton';
import { GlobalStyle } from '../globalStyles';
import { takeReturnTo } from '../utils/returnTo';
import VaccineTrackerPage from '../modules/vaccine-tracker/components/VaccineTrackerPage';

export const ME = gql`
  fragment MenuItem on MenuItem {
    label
    iconName
    linkUrl
    overrideMatchUrl
  }
  {
    me {
      _id
      firstName
      lastName
      title
      imageUrl
      emails {
        address
      }
      groups
      employeeType
      menu {
        ...MenuItem
        children {
          ...MenuItem
        }
      }
      inviteToBeta
      recentProjects
      hasPasswordCredentials
      supervisorId
      defaultRate
      supervisor {
        _id
        firstName
        lastName
        emails {
          address
        }
      }
      settings {
        useNewTaskSelector
        agGridColumns
        timesheetDailySnapshotOverrides
      }
    }
  }
`;

const SIDEBAR_WIDTH = 230;
const TOPBAR_HEIGHT = 75;
// const contentMaxWidth = 1115;
// const contentMinWidth = 970;

const routeTitleMap = [
  { pattern: /^\/user\/timesheets/, title: 'Timesheets' },
  { pattern: /^\/user\/profile/, title: 'Profile' },
  { pattern: /^\/project-management/, title: 'Project Management' },
  { pattern: /^\/vaccine-tracker/, title: 'Vaccine Tracker' },
  { pattern: /^\/finance/, title: 'Finance' },
  { pattern: /^\/admin/, title: 'Admin' },
  { pattern: /^\/super-admin/, title: 'Super Admin' },
  { pattern: /^\/performance/, title: 'Performance' },
  { pattern: /^\/human-resources/, title: 'Human Resources' },
];

function getTitleFromPath(pathname, defaultTitle) {
  const match = routeTitleMap.find(rt => rt.pattern.test(pathname));
  return match ? `${match.title} | ${defaultTitle}` : defaultTitle;
}

const MainLayout = ({ userTempPasswordCheck, pathname, search }) => {
  const [config] = useGlobal('config');
  const [user, setUser] = useGlobal('user');
  const [sidebarCollapsed, setSidebarCollapsed] = useState('sidebarCollapsed');
  const { loading, data } = useQuery(ME);
  const [networkStatus] = useGlobal('networkStatus');
  const { isOffline } = networkStatus;
  const location = useLocation();
  const defaultTitle = `${config['company-short-name']} Time Tracker`;
  const pageTitle = getTitleFromPath(location.pathname, defaultTitle);

  // useIdleTimer({
  //   timeout: 1000 * 60 * 30,
  //   onIdle: () => window.location.reload(),
  //   // onActive: handleOnActive,
  //   // onAction: handleOnAction,
  //   debounce: 500,
  // });

  // useEffect(() => {
  //   setTimeout(() => setSidebarCollapsed(!sidebarCollapsed), 3000);
  // }, []);

  // Someone who signed in to open a PDF from an approval email goes straight back to it.
  const signedIn = Boolean(data?.me);
  useEffect(() => {
    if (!signedIn) return;
    const returnTo = takeReturnTo();
    if (returnTo) window.location.replace(returnTo);
  }, [signedIn]);

  let hasTempPassword;
  if (userTempPasswordCheck) {
    hasTempPassword = userTempPasswordCheck.profile.tempPassword;
  }

  if (data?.me && !user) {
    setUser(data.me);
    datadogRum.setUser({
      id: data?.me._id,
      name: `${data?.me.firstName} ${data?.me.lastName}`,
      email: data?.me.emails[0].address,
    });
  }

  if (loading || !user) {
    return <TextLoader text="Loading user data" />;
  }

  const isSidebarHidden = sidebarCollapsed || hasTempPassword;

  return (
    <div className="h-screen overflow-hidden bg-[#f4f6f8]">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="icon" href={`${config['favicon-url']}`} sizes="32x32" />
      </Helmet>
      <GlobalStyle />
      <ReactResizeDetector
        handleWidth
        onResize={width => {
          if (width < 800 && !sidebarCollapsed) {
            setSidebarCollapsed(true);
          }

          if (width > 800 && sidebarCollapsed) {
            setSidebarCollapsed(false);
          }
        }}
      />
      {/* Fixed Top Bar */}
      <div 
        className="fixed top-0 left-0 right-0 z-[100] flex justify-center border-b border-[#dfe3e8] bg-[#121826]"
        style={{ height: TOPBAR_HEIGHT }}
      >
        <TopBar sidebarCollapsed={isSidebarHidden} />
      </div>

      {/* Main Layout */}
      <div
        className={classNames('flex w-full h-full justify-center', {
          'opacity-50 pointer-events-none select-none': isOffline,
        })}
      >
        <div className="flex w-full">
          {/* Fixed Sidebar */}
          <div
            className={classNames(
              'fixed left-0 bottom-0 z-[90] bg-white overflow-y-auto border-r-[1px] border-solid border-gray-300 transition-all duration-200',
              {
                'w-0 opacity-0': isSidebarHidden,
                'animate-fade-in': !isSidebarHidden,
              }
            )}
            style={{ 
              top: TOPBAR_HEIGHT,
              width: isSidebarHidden ? 0 : SIDEBAR_WIDTH,
            }}
          >
            <SideBar />
          </div>

          {/* Scrollable Content Area */}
          <main
            className="overflow-y-auto rounded-tl rounded-tr scrollbar-none"
            style={{
              marginTop: TOPBAR_HEIGHT,
              marginLeft: isSidebarHidden ? 0 : SIDEBAR_WIDTH,
              height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
              width: isSidebarHidden ? '100%' : `calc(100% - ${SIDEBAR_WIDTH}px)`,
              transition: 'margin-left 200ms, width 200ms',
            }}
          >
            <Switch>
              <Route exact path="/user" render={() => <Redirect to="/user/timesheets" />} />
              <Route exact path="/user/timesheets" component={TimesheetsPage} />
              <Route exact path="/user/timesheets/:endDate" component={TimesheetsPage} />
              <Route exact path="/user/profile" component={ProfilePage} />
              <Route path="/project-management" component={ProjectManagerPage} />
              <Route
                exact
                path="/vaccine-tracker"
                render={() => <Redirect to="/vaccine-tracker/proofs" />}
              />
              <Route exact path="/vaccine-tracker/proofs" component={VaccineTrackerPage} />
              <Route exact path="/vaccine-tracker/report" component={VaccineTrackerPage} />
              <Route path="/finance" component={FinancePage} />
              <Route path="/admin" component={AdminPage} />
              <Route path="/super-admin" component={SuperAdminPage} />
              <Route path="/performance" component={PerformancePage} />
              <Route path="/human-resources" component={HumanResourcesPage} />
            </Switch>
          </main>
        </div>
      </div>
      {/* <FeedbackButton /> */}
    </div>
  );
};

const mapStateToProps = state => ({
  userTempPasswordCheck: state.meteor.user,
  pathname: get(state, 'router.location.pathname'),
  search: get(state, 'router.location.search'),
});

export default connect(mapStateToProps)(MainLayout);
